/**
 * Filter panel for collection view
 */
import { useAtomValue } from 'jotai';
import { type ReactElement, useCallback, useState } from 'react';
import { IoClose } from 'react-icons/io5';

import CardFilterDropdown from '@/components/CardFilter/CardFilterDropdown';
import CmcFilterDropdown from '@/components/CmcFilter/CmcFilterDropdown';
import CollapsibleFilterGroup from '@/components/CollapsibleFilterGroup/CollapsibleFilterGroup';
import ColorFilterDropdown from '@/components/ColorFilter/ColorFilterDropdown';
import ColorIdentityFilterDropdown from '@/components/ColorIdentityFilter/ColorIdentityFilterDropdown';
import ConditionFilterDropdown from '@/components/ConditionFilter/ConditionFilterDropdown';
import FilterChip from '@/components/FilterChip/FilterChip';
import FilterDivider from '@/components/FilterDivider/FilterDivider';
import FinishFilterDropdown from '@/components/FinishFilter/FinishFilterDropdown';
import InDeckFilter, { type InDeckFilterValue } from '@/components/InDeckFilter/InDeckFilter';
import InListFilter, { type InListFilterValue } from '@/components/InListFilter/InListFilter';
import KeywordFilterDropdown from '@/components/KeywordFilter/KeywordFilterDropdown';
import RarityFilterDropdown from '@/components/RarityFilter/RarityFilterDropdown';
import RoleFilterDropdown from '@/components/RoleFilter/RoleFilterDropdown';
import SuperTypeFilterDropdown from '@/components/SuperTypeFilter/SuperTypeFilterDropdown';
import TypeFilterDropdown from '@/components/TypeFilter/TypeFilterDropdown';
import { decksStateAtom } from '@/features/decks/state/decksAtom';
import { listsStateAtom } from '@/features/lists/state/listsAtom';
import type { CollectionFilters } from '@/types/schemas';

import SetFilterDropdown from './SetFilterDropdown';

export interface FilterPanelProps {
  filters: CollectionFilters;
  setFilter: <K extends keyof CollectionFilters>(key: K, value: CollectionFilters[K]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

// Default collapsed state for remaining collapsible groups
const DEFAULT_COLLAPSED_STATE: Record<string, boolean> = {
  deck: true, // Closed
  list: true, // Closed
};

export default function FilterPanel({ filters, setFilter, clearFilters, hasActiveFilters }: FilterPanelProps): ReactElement {
  // Collapse state - ephemeral (resets on navigation)
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>(DEFAULT_COLLAPSED_STATE);

  // Decks for InDeckFilter
  const decksState = useAtomValue(decksStateAtom);
  const decks = decksState.data ?? [];

  // Lists for InListFilter
  const listsState = useAtomValue(listsStateAtom);
  const lists = listsState.data ?? [];

  // Reset collapsed state to defaults
  const resetCollapsedState = useCallback(() => {
    setCollapsedGroups(DEFAULT_COLLAPSED_STATE);
  }, []);

  // Calculate isLocked per group (locked when has active filters)
  const isDeckLocked = !!filters.inDeckFilter;
  const isListLocked = !!filters.inListFilter;

  const toggleArrayFilter = <T extends string | number>(key: keyof CollectionFilters, value: T, currentValues: T[] | undefined) => {
    const current = currentValues ?? [];
    const newValues = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setFilter(key, newValues.length > 0 ? (newValues as CollectionFilters[typeof key]) : undefined);
  };

  // CMC toggle: convert string values from CmcFilterDropdown to the typed CmcFilter values (number | '6+')
  const toggleCmcFromDropdown = (value: string) => {
    const typed = value === '6+' ? '6+' : Number(value);
    toggleArrayFilter('cmcValues', typed as string | number, filters.cmcValues as (string | number)[] | undefined);
  };

  const toggleCollapsed = (group: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  // Collapsed state (locked groups ignore this)
  const isDeckCollapsed = collapsedGroups.deck ?? DEFAULT_COLLAPSED_STATE.deck;
  const isListCollapsed = collapsedGroups.list ?? DEFAULT_COLLAPSED_STATE.list;

  // Handle clear: reset filters and collapsed state
  const handleClear = useCallback(() => {
    clearFilters();
    resetCollapsedState();
  }, [clearFilters, resetCollapsedState]);

  // Convert CMC filter values to string[] for the dropdown
  const cmcSelectedStrings = (filters.cmcValues ?? []).map(String);

  return (
    <div className="border-storm/50 bg-slate/30 flex flex-wrap items-center gap-2 rounded-lg border px-4 py-3">
      {/* Color filter dropdown */}
      <ColorFilterDropdown
        colors={filters.colors ?? []}
        specialColors={filters.specialColors}
        onColorToggle={(color) => toggleArrayFilter('colors', color, filters.colors)}
        onSpecialColorToggle={(special) => toggleArrayFilter('specialColors', special, filters.specialColors)}
        onClear={() => {
          setFilter('colors', undefined);
          setFilter('specialColors', undefined);
        }}
      />

      <FilterDivider />

      {/* Color identity filter dropdown */}
      <ColorIdentityFilterDropdown
        selectedIdentities={filters.colorIdentities ?? []}
        onToggle={(identity) => toggleArrayFilter('colorIdentities', identity, filters.colorIdentities)}
        onClear={() => setFilter('colorIdentities', undefined)}
      />

      <FilterDivider />

      {/* Set filter dropdown */}
      <SetFilterDropdown value={filters.sets} onChange={(setCodes) => setFilter('sets', setCodes)} />

      <FilterDivider />

      {/* Super type filter dropdown */}
      <SuperTypeFilterDropdown
        selectedSuperTypes={filters.superTypes ?? []}
        onToggle={(st) => toggleArrayFilter('superTypes', st, filters.superTypes)}
        onClear={() => setFilter('superTypes', undefined)}
      />

      <FilterDivider />

      {/* Card type filter dropdown */}
      <TypeFilterDropdown
        selectedTypes={filters.cardTypes ?? []}
        onToggle={(type) => toggleArrayFilter('cardTypes', type, filters.cardTypes)}
        onClear={() => setFilter('cardTypes', undefined)}
      />

      <FilterDivider />

      {/* Card category filter dropdown */}
      <CardFilterDropdown
        selectedCategories={filters.cardCategories ?? []}
        onToggle={(cat) => toggleArrayFilter('cardCategories', cat, filters.cardCategories)}
        onClear={() => setFilter('cardCategories', undefined)}
      />

      <FilterDivider />

      {/* Rarity filter dropdown */}
      <RarityFilterDropdown
        selectedRarities={filters.rarities ?? []}
        onToggle={(rarity) => toggleArrayFilter('rarities', rarity, filters.rarities)}
        onClear={() => setFilter('rarities', undefined)}
      />

      <FilterDivider />

      {/* CMC filter dropdown */}
      <CmcFilterDropdown
        selectedValues={cmcSelectedStrings}
        onToggle={toggleCmcFromDropdown}
        onClear={() => setFilter('cmcValues', undefined)}
      />

      <FilterDivider />

      {/* Role filter dropdown */}
      <RoleFilterDropdown
        selectedRoles={filters.roles ?? []}
        onToggle={(roleId) => toggleArrayFilter('roles', roleId, filters.roles)}
        onClear={() => setFilter('roles', undefined)}
      />

      <FilterDivider />

      {/* Keyword filter dropdown (self-contained open/close) */}
      <KeywordFilterDropdown
        selectedKeywords={filters.keywords ?? []}
        onToggle={(kw) => toggleArrayFilter('keywords', kw, filters.keywords)}
        onClear={() => setFilter('keywords', undefined)}
      />

      <FilterDivider />

      {/* Condition filter dropdown */}
      <ConditionFilterDropdown
        selectedConditions={filters.conditions ?? []}
        onToggle={(condition) => toggleArrayFilter('conditions', condition, filters.conditions)}
        onClear={() => setFilter('conditions', undefined)}
      />

      <FilterDivider />

      {/* Finish filter dropdown */}
      <FinishFilterDropdown
        selectedFinishes={filters.finishes ?? []}
        onToggle={(finish) => toggleArrayFilter('finishes', finish, filters.finishes)}
        onClear={() => setFilter('finishes', undefined)}
      />

      <FilterDivider />

      {/* Deck membership filter - collapsible */}
      {decks.length > 0 && (
        <>
          <CollapsibleFilterGroup
            label="Deck"
            isCollapsed={isDeckCollapsed}
            activeCount={filters.inDeckFilter ? 1 : 0}
            onToggle={() => toggleCollapsed('deck')}
            isLocked={isDeckLocked}
          >
            <InDeckFilter
              value={filters.inDeckFilter as InDeckFilterValue | undefined}
              deckId={filters.deckIdFilter}
              onValueChange={(value) => setFilter('inDeckFilter', value)}
              onDeckChange={(deckId) => setFilter('deckIdFilter', deckId)}
              decks={decks}
            />
          </CollapsibleFilterGroup>
          <FilterDivider />
        </>
      )}

      {/* List membership filter - collapsible */}
      {lists.length > 0 && (
        <>
          <CollapsibleFilterGroup
            label="List"
            isCollapsed={isListCollapsed}
            activeCount={filters.inListFilter ? 1 : 0}
            onToggle={() => toggleCollapsed('list')}
            isLocked={isListLocked}
          >
            <InListFilter
              value={filters.inListFilter as InListFilterValue | undefined}
              listId={filters.listIdFilter}
              onValueChange={(value) => setFilter('inListFilter', value)}
              onListChange={(listId) => setFilter('listIdFilter', listId)}
              lists={lists}
            />
          </CollapsibleFilterGroup>
          <FilterDivider />
        </>
      )}

      {/* Starred filter */}
      <FilterChip
        label="★ Starred"
        isActive={filters.starredOnly ?? false}
        onClick={() => setFilter('starredOnly', filters.starredOnly ? undefined : true)}
      />
      <FilterChip
        label="Game Changers"
        isActive={filters.gameChangersOnly ?? false}
        onClick={() => setFilter('gameChangersOnly', filters.gameChangersOnly ? undefined : true)}
      />
      <FilterChip
        label="Available"
        isActive={filters.availableOnly ?? false}
        onClick={() => setFilter('availableOnly', filters.availableOnly ? undefined : true)}
        title="Cards you own with copies remaining across all printings after usage in non-archived decks (maybeboard excluded)."
      />

      {/* Clear filters - pushed to the right */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={handleClear}
          className="text-danger hover:bg-danger/10 focus-visible:ring-danger focus-visible:ring-offset-void ml-auto flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          aria-label="Clear all filters"
        >
          <IoClose className="h-4 w-4" aria-hidden="true" />
          Clear
        </button>
      )}
    </div>
  );
}
